import type {
	ProductColorGroup,
	ProductNameGroup,
	ProductSize,
} from '../../utils/productGrouping'
import { getSortedSizeColumns } from '../../utils/productGrouping'

/**
 * Product payload the Shop Terminal extras tally already understands. Kept
 * identical to the scanner path so report labels stay consistent.
 */
export interface ExtraPickerProduct {
	id: string
	sku: string | null
	name: string | null
	image_url: string | null
	reference_code: string | null
	barcode: string | null
	color: string
	size: string
}

export const MODEL_RENDER_CAP = 60

/** Matches the scanner path: missing colour becomes an empty string, not '-'. */
export function displayColorName(colorName: string): string {
	return colorName === '-' ? '' : colorName
}

/** Matches the scanner path: the synthetic 'No Size' key becomes ''. */
export function displaySizeName(sizeName: string): string {
	return sizeName === 'No Size' ? '' : sizeName
}

/**
 * A colour whose only size key is the synthetic 'No Size' placeholder has no
 * real size variants — the colour step itself is the last selection.
 */
export function colorHasRealSizes(color: ProductColorGroup): boolean {
	const keys = Object.keys(color.sizeItemIds)
	if (keys.length === 0) return false
	return !(keys.length === 1 && keys[0] === 'No Size')
}

export function shouldSkipColorStep(group: ProductNameGroup): boolean {
	return group.colors.length === 1
}

export function shouldSkipSizeStep(color: ProductColorGroup): boolean {
	return !colorHasRealSizes(color)
}

/** Ordered size names for a single model, respecting product_sizes.sort_order. */
export function sortedSizesForGroup(
	group: ProductNameGroup,
	sizes: ProductSize[]
): string[] {
	return getSortedSizeColumns([group], sizes)
}

/**
 * Resolves a concrete product from a model × colour × size selection.
 * Returns null when the cell has no product id (empty matrix cell).
 */
export function resolveExtraProduct(
	group: ProductNameGroup,
	color: ProductColorGroup,
	sizeName: string
): ExtraPickerProduct | null {
	const productId = color.sizeItemIds[sizeName]
	if (!productId) return null

	const item = group.variants.find((variant) => variant.id === productId)
	if (!item) return null

	return {
		id: item.id,
		sku: item.sku ?? null,
		name: item.name ?? null,
		image_url: item.image_url ?? null,
		reference_code: item.reference_code ?? null,
		barcode: item.barcode ?? null,
		color: displayColorName(color.colorName),
		size: displaySizeName(sizeName),
	}
}

/**
 * When a colour has no real sizes, the only selectable product is the
 * synthetic 'No Size' cell (or the sole size key if somehow empty-named).
 */
export function resolveColorOnlyProduct(
	group: ProductNameGroup,
	color: ProductColorGroup
): ExtraPickerProduct | null {
	const sizeName = Object.keys(color.sizeItemIds)[0]
	if (!sizeName) return null
	return resolveExtraProduct(group, color, sizeName)
}
